"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { io, type Socket } from "socket.io-client";
import { api, type LiquiditySnapshot } from "@/lib/api";

interface MarketMidpointDisplayProps {
  slug: string;
  // 2026-05-09 — Polymarket parity rule: when bid/ask spread > 10¢
  // the midpoint is misleading (sparse book — the implied probability
  // sits halfway between two prices that may not actually be tradeable
  // close together). Polymarket falls back to the last-trade price
  // in that case. Pass the YES outcome's last-trade price so we can
  // render it as the headline when spread is wide. Optional — when
  // missing or null, we keep the prior midpoint-or-hide behaviour.
  lastTradePriceCents?: number | null;
}

// Polymarket rule: spread > $0.10 → display last-trade instead of
// midpoint. Their docs say "If the bid-ask spread is wider than
// $0.10, Polymarket displays the last traded price instead of the
// midpoint." Same threshold here.
const WIDE_SPREAD_THRESHOLD_CENTS = 10;

interface IncomingOrderbookEvent {
  marketSlug: string;
}

/**
 * EPIC-027 STORY-692-display — orderbook midpoint + spread on the
 * market detail page.
 *
 * 2026-05-09 — founder direction: the midpoint + spread must update
 * dynamically as the book moves. Two independent refresh sources to
 * survive each other's failures:
 *
 *   1. WS subscriber on `market:orderbook` + `market:trade` for the
 *      same slug. When the WS pipe is healthy this gives sub-second
 *      latency on price updates.
 *   2. Polling fallback every 5s. Founder reported 2026-05-09 that
 *      the chip wasn't refreshing on staging — root cause was the
 *      WS upgrade failing at the cloudflared tunnel ("WebSocket is
 *      closed before the connection is established"). Polling is the
 *      safety net that keeps the chip live regardless of WS state.
 *
 * Both paths debounce into the same 600ms refetch window, so a healthy
 * WS + the polling timer don't fan out into a request flood.
 *
 * Reads `/v1/markets/:slug/liquidity`. Hides itself when:
 *   - the API returns null (no orderbook activity yet — fresh market)
 *   - midpointCents is null on the snapshot (one-sided book)
 *   - the fetch fails (silent — this row is decorative)
 */
const POLL_INTERVAL_MS = 5_000;

export function MarketMidpointDisplay({
  slug,
  lastTradePriceCents,
}: MarketMidpointDisplayProps) {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState<LiquiditySnapshot | null>(null);
  const [loaded, setLoaded] = useState(false);
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchSnapshot() {
      try {
        const res = await api.getLiquidityForMarket(slug);
        if (cancelled) return;
        setSnapshot(res);
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
    }

    function scheduleRefetch() {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      refetchTimerRef.current = setTimeout(() => {
        void fetchSnapshot();
      }, 600);
    }

    void fetchSnapshot();

    // Polling fallback — runs always, doesn't depend on WS health.
    // Page-visibility check skips fetches when the tab is in the
    // background so we don't burn the user's data plan + the API
    // while they're not looking.
    pollIntervalRef.current = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      void fetchSnapshot();
    }, POLL_INTERVAL_MS);

    // WS subscriber — refetch on every orderbook change for sub-
    // second latency when the WS pipe is healthy. Wrapped in try/
    // catch + transports include 'polling' so a transport-level
    // failure doesn't crash the component.
    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    let socket: Socket | null = null;
    if (apiBase) {
      try {
        const url = apiBase.replace(/\/$/, "");
        socket = io(url, {
          transports: ["websocket", "polling"],
          reconnection: true,
          reconnectionAttempts: 3,
          // Don't spam the console with reconnect-attempt errors —
          // the polling fallback above keeps the chip live even if
          // WS is permanently down (cloudflared tunnel issue, etc.).
          timeout: 8000,
        });
        socket.on("connect", () => {
          socket?.emit("subscribe", { slug });
        });
        const onMarketEvent = (event: IncomingOrderbookEvent) => {
          if (event.marketSlug !== slug) return;
          scheduleRefetch();
        };
        socket.on("market:orderbook", onMarketEvent);
        // market:trade also shifts top-of-book when a maker is consumed,
        // so subscribe to both for completeness.
        socket.on("market:trade", onMarketEvent);
        // Swallow connection errors — the polling fallback handles
        // dynamic updates regardless of WS state.
        socket.on("connect_error", () => {});
      } catch {
        // socket-io constructor itself shouldn't throw, but be defensive.
      }
    }

    return () => {
      cancelled = true;
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (socket) {
        try {
          socket.emit("unsubscribe", { slug });
          socket.disconnect();
        } catch {
          // ignore — cleanup-time failures are harmless.
        }
      }
    };
  }, [slug]);

  if (!loaded) return null;
  if (!snapshot) return null;

  // 2026-05-09 — Polymarket parity: when bid/ask spread > 10¢, the
  // midpoint sits halfway between two prices that may not be tradeable
  // close together. Fall back to the last-trade price (when available)
  // and label the chip accordingly so the user knows what they're
  // looking at.
  const wideSpread =
    snapshot.spreadCents !== null &&
    snapshot.spreadCents > WIDE_SPREAD_THRESHOLD_CENTS;
  const useLastTrade =
    wideSpread &&
    typeof lastTradePriceCents === "number" &&
    Number.isFinite(lastTradePriceCents);

  // When we don't have a midpoint AND we don't have a last-trade
  // fallback, hide the chip — same posture as before.
  if (snapshot.midpointCents === null && !useLastTrade) return null;

  const headlineCents = useLastTrade
    ? (lastTradePriceCents as number)
    : (snapshot.midpointCents as number);
  const headlineLabel = useLastTrade
    ? t("marketMidpoint.lastTradeLabel", "Last trade")
    : t("marketMidpoint.label", "Midpoint");

  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.6px] text-[var(--color-text3)]">
        {headlineLabel}
      </div>
      <div
        className="mt-1 flex items-baseline gap-1 font-mono tabular-nums"
        data-testid="market-midpoint"
      >
        <span className="text-[20px] font-semibold text-[var(--color-text2)]">
          {headlineCents}
        </span>
        <span className="text-[12px] font-semibold text-[var(--color-text3)]">
          ¢
        </span>
        {snapshot.spreadCents !== null && (
          <span
            className="ml-2 text-[10px] font-semibold uppercase tracking-[0.6px] text-[var(--color-text3)]"
            title={
              wideSpread
                ? t(
                    "marketMidpoint.wideSpreadTooltip",
                    "Spread is wide ({{cents}}¢) — showing last-trade price instead of midpoint",
                    { cents: snapshot.spreadCents },
                  )
                : t("marketMidpoint.spreadTooltip", "Best ask minus best bid")
            }
          >
            {t("marketMidpoint.spreadTemplate", { cents: snapshot.spreadCents })}
          </span>
        )}
      </div>
    </div>
  );
}
